import { useState } from "react"

import { useParams } from "react-router-dom"

import { useMutation } from "@tanstack/react-query"

import { Mail, KeyRound, ShieldCheck, ArrowLeft } from "lucide-react"

import requestConsumerReportCodeMutation from "./services/request-consumer-report-code"
import retrieveConsumerReportMutation from "./services/retrieve-consumer-report"

import TruCheckConsumerReport from "../../Components/TruCheckConsumerReport"
import PublicHeader from "../../publicHeader"

const STEP = {
    EMAIL: "email",
    CODE: "code",
    REPORT: "report",
}

const CandidateConsumerReport = () => {
    const { reportKey } = useParams()

    const [step, setStep] = useState(STEP.EMAIL)
    const [email, setEmail] = useState("")
    const [code, setCode] = useState("")

    const {
        mutate: requestCode,
        isPending: isRequestingCode,
        error: requestCodeError,
    } = useMutation({
        mutationFn: requestConsumerReportCodeMutation,
        onSuccess: () => setStep(STEP.CODE),
    })

    const {
        mutate: retrieveReport,
        isPending: isRetrievingReport,
        error: retrieveReportError,
        data: report,
    } = useMutation({
        mutationFn: retrieveConsumerReportMutation,
        onSuccess: () => setStep(STEP.REPORT),
    })

    const onSubmitEmail = (e) => {
        e.preventDefault()
        if (!email.trim()) return
        requestCode({ reportKey, email: email.trim() })
    }

    const onSubmitCode = (e) => {
        e.preventDefault()
        if (!code.trim()) return
        retrieveReport({ reportKey, code: code.trim() })
    }

    // STEP 3 — the report itself, rendered with the shared TruCheckConsumerReport UI.
    if (step === STEP.REPORT) {
        return (
            <div className="min-h-screen bg-gray-50">
                <PublicHeader />
                <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
                    <TruCheckConsumerReport data={report} />
                </main>
            </div>
        )
    }

    const isEmailStep = step === STEP.EMAIL

    return (
        <div className="min-h-screen bg-gray-50">
            <PublicHeader />
            <div className="flex flex-col items-center justify-center px-4 py-10">
                <div className="w-full max-w-md">

                    {/* HEADER */}
                    <div className="flex flex-col items-center text-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl flex items-center justify-center mb-4">
                            <ShieldCheck className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">TruCheck™ Consumer Report</h1>
                        <p className="text-sm text-gray-500 mt-2">
                            {isEmailStep
                                ? `Enter the email associated with your TruCheck™ Consumer Report. (This is the email your report was sent to.)`
                                : "Enter the code we emailed you to view your report."}
                        </p>
                    </div>

                    {/* CARD */}
                    <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg p-6">

                        {isEmailStep ? (
                            <form onSubmit={onSubmitEmail} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Email address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@example.com"
                                            autoComplete="email"
                                            required
                                            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition"
                                        />
                                    </div>
                                </div>

                                {requestCodeError && (
                                    <p className="text-sm text-red-600">
                                        We couldn't verify that email for this report. Please check and try again.
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    disabled={isRequestingCode}
                                    className="w-full py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
                                >
                                    {isRequestingCode ? "Sending code..." : "Send code"}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={onSubmitCode} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        One-time code
                                    </label>
                                    <div className="relative">
                                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            placeholder="Enter code"
                                            autoComplete="one-time-code"
                                            required
                                            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition tracking-widest"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Sent to <span className="font-medium">{email}</span>
                                    </p>
                                </div>

                                {retrieveReportError && (
                                    <p className="text-sm text-red-600">
                                        That code is invalid or has expired. Please try again.
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    disabled={isRetrievingReport}
                                    className="w-full py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
                                >
                                    {isRetrievingReport ? "Verifying..." : "View report"}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setStep(STEP.EMAIL)}
                                    className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" />
                                    Use a different email
                                </button>
                            </form>
                        )}

                    </div>
                </div>
            </div>
        </div>
    )
}

export default CandidateConsumerReport
